import type { TreeNode, TreeNodeType, VersionedValue } from "./Types.ts";

// ─── HTML → TreeNode converter ───────────────────────────────────────────────
//
// Parses an HTML string or a live DOM Element into a TreeNode subtree.
// Every node receives a freshly generated UUID v4 as its id, which is also
// written into the node's attributes as data-rd-id so the iframe bridge can
// locate the real DOM element via querySelector('[data-rd-id="<uuid>"]').
//
// If a node already carries a data-rd-id attribute (e.g. round-tripping an
// existing page), that value is reused instead of generating a new one.

// ─── Public entry points ─────────────────────────────────────────────────────

/**
 * Parses an HTML string and returns the children that should be placed under
 * the tree's root document node. The <html> element (if present) becomes the
 * first child.
 */
export function parseHTMLString(html: string, userId: string): TreeNode[] {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    return parseChildNodes(doc.documentElement ? [doc.documentElement] : Array.from(doc.childNodes), userId, "");
}

/**
 * Converts a single live DOM Element (and its descendants) into a TreeNode
 * subtree. Useful when the iframe reports a user-initiated structural change.
 */
export function parseElement(element: Element, userId: string, removeIds: boolean = false): TreeNode {
    return convertElement(element, userId, "", removeIds);
}

// ─── Internal conversion ─────────────────────────────────────────────────────

function parseChildNodes(nodes: Node[] | NodeListOf<ChildNode>, userId: string, parentId: string, removeIds: boolean = false): TreeNode[] {
    const result: TreeNode[] = [];
    for (const node of Array.from(nodes)) {
        const converted = convertNode(node, userId, parentId, removeIds);
        if (converted !== null) {
            result.push(converted);
        }
    }
    return result;
}

function convertNode(node: Node, userId: string, parentId: string, removeIds: boolean = false): TreeNode | null {
    if (node.nodeType === Node.ELEMENT_NODE) {
        return convertElement(node as Element, userId, parentId, removeIds);
    }
    if (node.nodeType === Node.TEXT_NODE) {
        return convertTextNode(node as Text, userId, parentId, removeIds);
    }
    if (node.nodeType === Node.COMMENT_NODE) {
        return convertCommentNode(node as Comment, userId, parentId, removeIds);
    }
    // Other node types (processing instructions, doctype, etc.) are skipped.
    return null;
}

function convertElement(element: Element, userId: string, parentId: string, removeIds: boolean = false): TreeNode {
    // Reuse an existing data-rd-id if the element already has one.
    if(removeIds) {
        element.removeAttribute("data-rd-id");
    }
    const existingId = element.getAttribute("data-rd-id");
    const id = existingId && existingId.trim().length > 0 ? existingId.trim() : crypto.randomUUID();
   const now = Date.now();
    const tagName = element.tagName.toLowerCase();

    const node: TreeNode = {
        id,
        type: "element",
        parentId,
        position: "",
        tagName: makeVersioned(tagName),
        text: makeVersioned(null),
        comment: makeVersioned(null),
        attributes: new Map(),
        classes: new Map(),
        customStyles: new Map(),
        children: [],
        createdUserId: userId,
        createdAt: now,
        updatedUserId: userId,
        updatedAt: now,
    };

    // Convert attributes.
    for (const attr of Array.from(element.attributes)) {
        if (attr.name === "class") {
            // Classes are stored in the classes Map, not in attributes.
            continue;
        }
        node.attributes.set(attr.name.toLowerCase(), makeVersioned<string | null>(attr.value));
    }

    // Always write data-rd-id so the iframe bridge can find this element.
   // node.attributes.set("data-rd-id", makeVersioned<string | null>(id));

    // Convert class names into individual entries in the classes Map.
    const classAttr = element.getAttribute("class");
    if (classAttr) {
        for (const className of classAttr.split(/\s+/).filter(Boolean)) {
            node.classes.set(className, makeVersioned(true));
        }
    }

    // Recursively convert children.
    const children = parseChildNodes(element.childNodes, userId, id, removeIds);
    node.children = children;

    return node;
}

function convertTextNode(textNode: Text, userId: string, parentId: string, removeIds: boolean = false): TreeNode {
    if (removeIds) {
        textNode["data-rd-id"] = undefined;
    }
    const id = textNode["data-rd-id"] ?? crypto.randomUUID();
    const now = Date.now();

    return {
        id,
        type: "text",
        parentId,
        position: "",
        tagName: makeVersioned(""),
        text: makeVersioned<string | null>(textNode.textContent ?? ""),
        comment: makeVersioned(null),
        attributes: new Map(),
        classes: new Map(),
        customStyles: new Map(),
        children: [],
        createdUserId: userId,
        createdAt: now,
        updatedUserId: userId,
        updatedAt: now,
    };
}

function convertCommentNode(commentNode: Comment, userId: string, parentId: string, removeIds: boolean = false): TreeNode {
    if (removeIds) {
        commentNode["data-rd-id"] = undefined;
    }
    const id = commentNode["data-rd-id"] ?? crypto.randomUUID();
    const now = Date.now();

    return {
        id,
        type: "comment",
        parentId,
        position: "",
        tagName: makeVersioned(""),
        text: makeVersioned(null),
        comment: makeVersioned<string | null>(commentNode.textContent ?? ""),
        attributes: new Map(),
        classes: new Map(),
        customStyles: new Map(),
        children: [],
        createdUserId: userId,
        createdAt: now,
        updatedUserId: userId,
        updatedAt: now,
    };
}

// ─── After JSON round-trip ────────────────────────────────────────────────────
//
// JSON.parse turns Maps into plain objects. Call restoreNodeMaps on every node
// that arrives from a JSON payload (snapshot, add patch) before passing it to
// the tree.

export function restoreNodeMaps(node: TreeNode): void {
    if (!(node.attributes instanceof Map)) {
        node.attributes = new Map(Object.entries(node.attributes as unknown as Record<string, VersionedValue<string | null>>));
    }
    if (!(node.classes instanceof Map)) {
        node.classes = new Map(Object.entries(node.classes as unknown as Record<string, VersionedValue<boolean>>));
    }
    if (!(node.customStyles instanceof Map)) {
        node.customStyles = new Map(Object.entries(node.customStyles as unknown as Record<string, VersionedValue<string>>));
    }
    for (const child of node.children ?? []) {
        restoreNodeMaps(child);
    }
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function makeVersioned<T>(value: T): VersionedValue<T> {
    return { version: 0, seq: 0, value };
}
